<template>
  <span v-if="showIcon" class="IntrusionZoneSmartIcon" v-tooltip="tooltip">
    <FaIcon :icon="view.icon" :class="colorClass" />
    <span v-if="showBadge" class="IntrusionZoneSmartIcon__count">{{ view.count }}</span>
  </span>
  <span v-else-if="showLabel" :class="colorClass">{{ view.label }}</span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { ICON } from '@/constants/icons';
import type { IntrusionZoneStatusFlag, ZonePartitionArmState } from '@/views/intrusion/types';
import {
  buildZoneSmartIconSummary,
  getZoneAggregateDescriptor,
  type IntrusionZoneSmartIconSummary
} from '@/views/intrusion/utilities/intrusion-smart-icon-status';
import { getZoneStatusFlags } from '@/views/intrusion/utilities/map-intrusion';
import { getStatusCellVariant } from '@/views/intrusion/utilities/status-variant';

defineOptions({ name: 'IntrusionZoneSmartIcon' });

/** A raw zone row (as returned by a device/panel query): the icon decodes its own status flags. */
type ZoneStatusSource = Parameters<typeof getZoneStatusFlags>[0];

const props = withDefaults(
  defineProps<{
    /** Single zone: its own status string + flags. */
    status?: string;
    statusFlags?: readonly IntrusionZoneStatusFlag[];
    partitionArmState?: ZonePartitionArmState;
    isSyncing?: boolean;
    /** Aggregate: a count summary of many zones. Drives the label/colour instead when set. */
    summary?: IntrusionZoneSmartIconSummary;
    zones?: readonly ZoneStatusSource[];
    /** Overlay the count on the glyph (aggregate contexts with no separate count beside it). */
    showCount?: boolean;
    type?: 'icon' | 'label';
  }>(),
  { type: 'icon' }
);

// A pre-built `summary` wins; otherwise raw `zones` are decoded + summarised here.
const aggregateSummary = computed<IntrusionZoneSmartIconSummary | undefined>(() => {
  if (props.summary) return props.summary;
  if (!props.zones) return undefined;

  return buildZoneSmartIconSummary(props.zones.map((zone) => ({ statusFlags: getZoneStatusFlags(zone) })));
});

const view = computed(() => {
  if (aggregateSummary.value) {
    return { ...getZoneAggregateDescriptor(aggregateSummary.value), prefix: 'Zones' };
  }

  return {
    variant: getStatusCellVariant(Boolean(props.isSyncing), props.statusFlags ?? [], {
      partitionArmState: props.partitionArmState
    }),
    icon: ['fas', ICON.SENSOR] as ['fas', string],
    label: props.isSyncing ? 'Syncing...' : props.status || 'Unknown',
    count: 0,
    prefix: 'Zone'
  };
});

const colorClass = computed(() => `IntrusionStatusColor--${view.value.variant}`);
const showIcon = computed(() => props.type === 'icon');
const showLabel = computed(() => props.type === 'label');
const showBadge = computed(() => Boolean(props.showCount) && view.value.count > 1);
const tooltip = computed(() => ({
  triggers: ['click', 'hover', 'focus'],
  content: `${view.value.prefix}: ${view.value.label}`
}));
</script>

<style scoped lang="scss">
.IntrusionZoneSmartIcon {
  position: relative;
  display: inline-flex;

  &__count {
    position: absolute;
    top: calc(-1 * var(--size-1));
    right: calc(-1 * var(--size-2));
    min-width: var(--size-4);
    height: var(--size-4);
    padding: 0 var(--size-1);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--size-4);
    background: var(--color-background-600);
    color: var(--color-text);
    font-size: 0.65rem;
    font-weight: $font-bold;
    line-height: 1;
  }
}
</style>
